import.meta.glob([
  '../images/**',
  '../fonts/**',
]);

// Alpine.js
import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';
import intersect from '@alpinejs/intersect';

// Make Alpine available globally for debugging
window.Alpine = Alpine;

// Alpine.js Plugins
Alpine.plugin(collapse);
Alpine.plugin(intersect);

// Import custom components
import featureShowcase from './components/feature-showcase';
import testimonialCarousel from './components/testimonial-carousel';
import PureOneAnimations from './pureone-animations';
import PureOnePerformance from './pureone-performance';
import PureOneInteractions from './pureone-interactions';
import FeatureStacking from './components/feature-stacking'; // GSAP-powered stacking animations
import PureOneHeroScroll from './pureone-hero-scroll'; // GSAP-powered hero scroll animation

// GA4 event tracking
import { initTracking } from './tracking';

// Lenis smooth scroll
import { initLenis, pauseLenis, resumeLenis } from './animation/core/lenis';

// Scroll animations controller
import { initScrollAnimations } from './animation/core/scroll-controller';

// Alpine.js Components
window.Alpine = Alpine;

// Register custom Alpine components
Alpine.data('featureShowcase', featureShowcase);

// Testimonial Carousel - Using inline definition for now
Alpine.data('testimonialCarousel', () => ({
    currentIndex: 0,
    slideWidth: 100,
    testimonials: [],
    autoplayInterval: null,
    isPaused: false,
    totalSlides: 0,

    init() {
      this.updateSlideWidth();
      window.addEventListener('resize', () => this.updateSlideWidth());

      const slides = this.$el.querySelectorAll('[data-testimonial-slide], .testimonial-slide');
      this.totalSlides = slides.length;

      this.startAutoplay();

      const container = this.$el;
      container.addEventListener('mouseenter', () => this.pauseAutoplay());
      container.addEventListener('mouseleave', () => this.resumeAutoplay());

      this.initTouchSupport();
    },

    updateSlideWidth() {
      if (window.innerWidth < 768) {
        this.slideWidth = 100;
      } else if (window.innerWidth < 1024) {
        this.slideWidth = 50;
      } else {
        this.slideWidth = 33.333;
      }
    },

    get maxIndex() {
      const visibleSlides = 100 / this.slideWidth;
      return Math.max(0, Math.ceil(this.totalSlides - visibleSlides));
    },

    get canScrollPrev() {
      return this.currentIndex > 0;
    },

    get canScrollNext() {
      return this.currentIndex < this.maxIndex;
    },

    prev() {
      if (this.canScrollPrev) {
        this.currentIndex--;
      }
    },

    next() {
      if (this.canScrollNext) {
        this.currentIndex++;
      } else {
        this.currentIndex = 0;
      }
    },

    goToSlide(index) {
      this.currentIndex = Math.min(index, this.maxIndex);
    },

    startAutoplay() {
      this.autoplayInterval = setInterval(() => {
        if (!this.isPaused) {
          this.next();
        }
      }, 5000);
    },

    pauseAutoplay() {
      this.isPaused = true;
    },

    resumeAutoplay() {
      this.isPaused = false;
    },

    initTouchSupport() {
      let startX = 0;
      let currentX = 0;
      let diff = 0;

      const track = this.$el.querySelector('.carousel-track');
      if (!track) return;

      track.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        this.pauseAutoplay();
      });

      track.addEventListener('touchmove', (e) => {
        currentX = e.touches[0].clientX;
        diff = startX - currentX;
      });

      track.addEventListener('touchend', () => {
        if (Math.abs(diff) > 50) {
          if (diff > 0 && this.canScrollNext) {
            this.next();
          } else if (diff < 0 && this.canScrollPrev) {
            this.prev();
          }
        }
        this.resumeAutoplay();
      });
    }
}));

// Legal Accordion Component - defined inline in app.blade.php due to CDN Alpine

// Alpine Data Components
Alpine.data('productGallery', () => ({
  currentImage: 0,
  isZoomed: false,
  totalImages: 5, // Will be set by the template
  init() {
    // Initialize with first image visible
    this.currentImage = 0;
  },
  setActiveImage(index) {
    this.currentImage = index;
  },
  nextImage() {
    this.currentImage = (this.currentImage + 1) % this.totalImages;
  },
  prevImage() {
    this.currentImage = (this.currentImage - 1 + this.totalImages) % this.totalImages;
  },
  toggleZoom() {
    this.isZoomed = !this.isZoomed;
  },
  imageLoaded() {
    // Called when image loads
  }
}));

Alpine.data('cartQuantity', () => ({
  quantity: 1,
  min: 1,
  max: 99,
  increase() {
    if (this.quantity < this.max) {
      this.quantity++;
      this.updateCart();
    }
  },
  decrease() {
    if (this.quantity > this.min) {
      this.quantity--;
      this.updateCart();
    }
  },
  updateCart() {
    // Trigger cart update via WooCommerce AJAX
    const event = new CustomEvent('cart-quantity-changed', { 
      detail: { quantity: this.quantity } 
    });
    window.dispatchEvent(event);
  }
}));

Alpine.data('formValidation', () => ({
  errors: {},
  validateField(field, value, rules) {
    const errors = [];
    
    if (rules.required && !value) {
      errors.push('This field is required');
    }
    
    if (rules.email && value && !this.isValidEmail(value)) {
      errors.push('Please enter a valid email address');
    }
    
    if (rules.minLength && value && value.length < rules.minLength) {
      errors.push(`Minimum ${rules.minLength} characters required`);
    }
    
    this.errors[field] = errors.length ? errors : null;
  },
  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  },
  clearError(field) {
    delete this.errors[field];
  },
  hasError(field) {
    return this.errors[field] && this.errors[field].length > 0;
  },
  getError(field) {
    return this.errors[field] ? this.errors[field][0] : '';
  }
}));

// Mobile Menu Component
Alpine.data('mobileMenu', () => ({
  open: false,
  toggle() {
    this.open = !this.open;
    // Prevent body scroll when menu is open
    document.body.style.overflow = this.open ? 'hidden' : '';
  },
  close() {
    this.open = false;
    document.body.style.overflow = '';
  }
}));

// Purchase Module Component (PureOne Product)
Alpine.data('purchaseModule', () => ({
  quantity: 1,
  isAdding: false,

  init() {
    this.quantity = 1;
    this.isAdding = false;
  },

  submitAddToCart(event) {
    const form = event.target;
    const productId = form.querySelector('input[name="product_id"]')?.value;
    const quantity = this.quantity;
    const component = this;

    if (!productId || this.isAdding) return;

    this.isAdding = true;

    // Use WooCommerce AJAX to add to cart
    const formData = new FormData();
    formData.append('action', 'woocommerce_add_to_cart');
    formData.append('product_id', productId);
    formData.append('quantity', quantity);

    fetch(wc_add_to_cart_params.wc_ajax_url.toString().replace('%%endpoint%%', 'add_to_cart'), {
      method: 'POST',
      body: formData
    })
    .then(response => response.json())
    .then(data => {
      if (data.error && data.product_url) {
        window.location = data.product_url;
        return;
      }

      // Success - trigger WooCommerce fragments refresh
      if (window.jQuery) {
        jQuery(document.body).trigger('added_to_cart', [data.fragments, data.cart_hash]);
      }

      // Redirect to checkout after adding to cart
      window.location.href = '/checkout/';
    })
    .catch(error => {
      console.error('Add to cart error:', error);
      // Fallback to normal form submission on error
      form.submit();
    })
    .finally(() => {
      component.isAdding = false;
    });
  }
}));

// Smart Purchase Bar Component
Alpine.data('smartPurchaseBar', () => {
  let dismissed = sessionStorage.getItem('purchaseBarDismissed') === 'true';

  return {
    visible: false,
    expanded: false,

    toggleExpand() {
      this.expanded = !this.expanded;
    },

    dismiss() {
      dismissed = true;
      this.visible = false;
      this.expanded = false;
      sessionStorage.setItem('purchaseBarDismissed', 'true');
    },

    init() {
      if (dismissed) return;

      // Use a throttled scroll check
      let ticking = false;
      const component = this;

      const updateVisibility = () => {
        if (!dismissed) {
          component.visible = window.scrollY > 100;
        }
        ticking = false;
      };

      window.addEventListener('scroll', () => {
        if (!ticking) {
          requestAnimationFrame(updateVisibility);
          ticking = true;
        }
      }, { passive: true });

      // Initial check
      updateVisibility();
    }
  };
});

// Article Feedback Component (Support Articles)
Alpine.data('articleFeedback', (postId) => ({
  postId: postId,
  submitted: false,
  loading: false,
  error: null,

  async submitFeedback(response) {
    if (this.loading || this.submitted) return;

    this.loading = true;
    this.error = null;

    try {
      const formData = new FormData();
      formData.append('action', 'article_feedback');
      formData.append('post_id', this.postId);
      formData.append('feedback', response);
      formData.append('nonce', window.dhArticleFeedback?.nonce || '');

      const ajaxUrl = window.dhArticleFeedback?.ajaxUrl || '/wp-admin/admin-ajax.php';

      const fetchResponse = await fetch(ajaxUrl, {
        method: 'POST',
        body: formData
      });

      const data = await fetchResponse.json();

      if (data.success) {
        this.submitted = true;
      } else {
        this.error = data.data?.message || 'Something went wrong. Please try again.';
        console.error('Article feedback error:', data);
      }
    } catch (err) {
      console.error('Article feedback error:', err);
      this.error = 'Something went wrong. Please try again.';
    } finally {
      this.loading = false;
    }
  }
}));

// Initialize Alpine - MUST be after all component registrations
Alpine.start();

// Mark Alpine as ready for CSS defensive fallbacks
document.addEventListener('alpine:initialized', () => {
  document.body.classList.add('alpine-ready');
  // Also add to all x-data elements
  document.querySelectorAll('[x-data]').forEach(el => {
    el.classList.add('x-ready');
  });
});

// Fallback: mark ready after a timeout if event doesn't fire
setTimeout(() => {
  if (!document.body.classList.contains('alpine-ready')) {
    document.body.classList.add('alpine-ready');
    console.log('[DH] Alpine ready marked via timeout fallback');
  }
}, 2000);

// Smooth Scroll Function
function initSmoothScroll() {
  const smoothScrollLinks = document.querySelectorAll('[data-smooth-scroll]');

  smoothScrollLinks.forEach((link) => {
    link.addEventListener('click', function(e) {
      e.preventDefault();

      const targetId = this.getAttribute('href');
      if (targetId && targetId.startsWith('#')) {
        const targetElement = document.querySelector(targetId);

        if (targetElement) {
          // Calculate offset for fixed header
          const header = document.querySelector('header');
          const headerHeight = header ? header.offsetHeight : 0;
          const targetPosition = targetElement.offsetTop - headerHeight - 20;

          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      }
    });
  });
}

// WooCommerce AJAX Cart Enhancement
document.addEventListener('DOMContentLoaded', function() {
  // GA4 event tracking (add_to_cart, CTA clicks, search, scroll depth)
  initTracking();

  // Initialize Lenis smooth scroll (falls back gracefully if reduced motion)
  const lenis = initLenis();

  // If Lenis didn't initialize (reduced motion), use legacy smooth scroll
  if (!lenis) {
    initSmoothScroll();
  }

  // Initialize scroll-triggered animations
  initScrollAnimations();
  // Product Gallery Image Switching
  const mainImage = document.getElementById('main-product-image');
  const thumbnailButtons = document.querySelectorAll('.thumbnail-btn');

  if (mainImage && thumbnailButtons.length > 0) {
    thumbnailButtons.forEach((button, index) => {
      button.addEventListener('click', function(e) {
        e.preventDefault();

        // Get the full-size image URL from the data attribute
        const fullImageUrl = this.dataset.fullImage;

        if (fullImageUrl) {
          // Update the main image
          mainImage.src = fullImageUrl;
          mainImage.srcset = fullImageUrl;

          // Update zoom data attribute if it exists
          if (mainImage.dataset.zoom) {
            mainImage.dataset.zoom = fullImageUrl;
          }
        } else {
          // Fallback: get the thumbnail image and try to get full size
          const thumbnailImg = this.querySelector('img');
          if (thumbnailImg) {
            // Remove size suffix from thumbnail URL to get full image
            const fullSrc = thumbnailImg.src.replace(/-\d+x\d+(\.\w+)$/, '$1');
            mainImage.src = fullSrc;
            mainImage.srcset = fullSrc;
          }
        }

        // Update active thumbnail styling
        thumbnailButtons.forEach(btn => {
          btn.classList.remove('border-[var(--brand-primary)]', 'ring-2', 'ring-[var(--brand-primary)]');
        });
        this.classList.add('border-[var(--brand-primary)]', 'ring-2', 'ring-[var(--brand-primary)]');
      });

      // Mark first thumbnail as active on page load
      if (index === 0) {
        button.classList.add('border-[var(--brand-primary)]', 'ring-2', 'ring-[var(--brand-primary)]');
      }
    });
  }

  // AJAX Add to Cart - Use WooCommerce's built-in AJAX system
  const addToCartButtons = document.querySelectorAll('.ajax_add_to_cart');

  addToCartButtons.forEach(button => {
    button.addEventListener('click', function(e) {
      e.preventDefault();

      const productId = this.dataset.product_id;
      const productName = this.dataset.product_name || 'Product';
      const quantity = this.dataset.quantity || 1;

      // Add loading state
      const originalText = this.textContent;
      this.textContent = 'Adding...';
      this.classList.add('loading');
      this.disabled = true;

      // Use WooCommerce's AJAX endpoint with proper parameters
      const formData = new FormData();
      formData.append('action', 'woocommerce_add_to_cart');
      formData.append('product_id', productId);
      formData.append('quantity', quantity);

      fetch(wc_add_to_cart_params.wc_ajax_url.toString().replace('%%endpoint%%', 'add_to_cart'), {
        method: 'POST',
        body: formData
      })
      .then(response => response.json())
      .then(data => {
        if (data.error && data.product_url) {
          window.location = data.product_url;
          return;
        }

        // Success - trigger WooCommerce fragments refresh
        // WooCommerce expects a jQuery object for the button parameter
        if (window.jQuery) {
          jQuery(document.body).trigger('added_to_cart', [data.fragments, data.cart_hash, jQuery(button)]);
        }
      })
      .catch(error => {
        console.error('Add to cart error:', error);
      })
      .finally(() => {
        // Remove loading state
        this.textContent = originalText;
        this.classList.remove('loading');
        this.disabled = false;
      });
    });
  });

  // PureONE Add to Cart - Track which buttons should open cart after add
  // Instead of duplicating AJAX, we flag buttons and open cart after WooCommerce's added_to_cart event
  let shouldOpenMiniCart = false;

  const pureoneAddToCartButtons = document.querySelectorAll('.pureone-add-to-cart');
  pureoneAddToCartButtons.forEach(button => {
    // Add WooCommerce AJAX classes so it uses built-in handler
    // Both classes are required for WooCommerce AJAX to work
    button.classList.add('add_to_cart_button', 'ajax_add_to_cart');

    button.addEventListener('click', function() {
      // Flag that we should open the cart after this add
      if (this.dataset.openCart === 'true') {
        shouldOpenMiniCart = true;
      }
    });
  });

  // Listen for WooCommerce's added_to_cart event and redirect to cart page if flagged
  if (window.jQuery) {
    jQuery(document.body).on('added_to_cart', function(event, fragments, cart_hash, button) {
      if (shouldOpenMiniCart) {
        shouldOpenMiniCart = false; // Reset flag

        // Redirect to checkout after product is added
        window.location.href = '/checkout/';
      }
    });
  }

  
  // Product Quantity Updates in Cart
  const quantityInputs = document.querySelectorAll('.qty');
  quantityInputs.forEach(input => {
    input.addEventListener('change', function() {
      const updateButton = document.querySelector('[name="update_cart"]');
      if (updateButton) {
        updateButton.disabled = false;
        updateButton.click();
      }
    });
  });

  // Checkout Coupon Toggle
  const couponToggle = document.querySelector('.showcoupon');
  const couponForm = document.querySelector('.checkout_coupon');

  if (couponToggle && couponForm) {
    couponToggle.addEventListener('click', function(e) {
      e.preventDefault();

      if (couponForm.style.display === 'none' || !couponForm.style.display) {
        couponForm.style.display = 'block';
        couponToggle.textContent = 'Hide coupon form';
      } else {
        couponForm.style.display = 'none';
        couponToggle.textContent = 'Have a coupon code?';
      }
    });
  }

  // Shop AJAX Filtering
  const shopOrderingForm = document.querySelector('.woocommerce-ordering');
  const shopOrderingSelect = document.querySelector('.woocommerce-ordering select.orderby');
  const productsGrid = document.querySelector('.products-grid');

  if (shopOrderingForm && shopOrderingSelect && productsGrid) {
    // Prevent form submission
    shopOrderingForm.addEventListener('submit', function(e) {
      e.preventDefault();
      return false;
    });

    // Handle select change
    shopOrderingSelect.addEventListener('change', function(e) {
      e.preventDefault();
      e.stopPropagation();

      const orderby = this.value;
      const currentUrl = new URL(window.location.href);

      // Update URL parameter
      currentUrl.searchParams.set('orderby', orderby);

      // Add loading state
      productsGrid.style.opacity = '0.5';
      productsGrid.style.pointerEvents = 'none';

      // Fetch updated products
      fetch(currentUrl.toString(), {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(response => response.text())
      .then(html => {
        // Parse the response HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newProductsGrid = doc.querySelector('.products-grid');

        if (newProductsGrid) {
          // Replace the products grid
          productsGrid.innerHTML = newProductsGrid.innerHTML;

          // Update the URL without reloading
          window.history.pushState({}, '', currentUrl.toString());

          // Scroll to products section smoothly
          const shopContent = document.querySelector('.shop-content');
          if (shopContent) {
            const header = document.querySelector('header');
            const headerHeight = header ? header.offsetHeight : 0;
            const targetPosition = shopContent.offsetTop - headerHeight - 20;

            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });
          }
        }
      })
      .catch(error => {
        console.error('Shop filtering error:', error);
        // Fallback to page reload on error
        window.location.href = currentUrl.toString();
      })
      .finally(() => {
        // Remove loading state
        productsGrid.style.opacity = '1';
        productsGrid.style.pointerEvents = 'auto';
      });

      return false;
    });
  }
});
